import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { animes } from "@/mocks/anime";
import EpisodeList from "./components/EpisodeList";
import CommentSection from "./components/CommentSection";

export default function Detail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const anime = animes.find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [currentEpisode, setCurrentEpisode] = useState(anime ? anime.currentEpisode : 1);
  const playerRef = useRef<HTMLDivElement>(null);

  useSeo({
    title: anime ? `${anime.title} - 详情 - 樱花动漫官网` : "动漫详情 - 樱花动漫官网",
    description: anime
      ? anime.intro.slice(0, 50)
      : "樱花动漫官网收录海量动漫作品，提供详细信息、剧集列表与相关推荐。",
    keywords: anime ? `${anime.title},${anime.genres.join(",")},樱花动漫官网` : "动漫详情,樱花动漫官网",
  });

  if (!anime) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[900px] px-4 py-20 md:px-8">
          <EmptyState
            title="没有找到这部作品"
            description="可能是链接里的编号有误，或者该作品已经下架。你可以回到分类页重新找一找。"
            actionLabel="返回动漫分类"
            onAction={() => navigate("/category")}
            secondaryLabel="回到首页"
            secondaryTo="/"
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorited = isFavorite(anime.id);
  const recommendations = animes
    .filter((item) => item.id !== anime.id && item.genres.some((genre) => anime.genres.includes(genre)))
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 4);

  const scrollToPlayer = () => {
    playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="relative overflow-hidden border-b border-background-200 bg-background-100">
          <img
            src={anime.cover}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-20 blur-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/60 via-background-50/80 to-background-100" />
          <div className="relative mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <Link to="/category" className="transition-colors hover:text-primary-600">
                动漫分类
              </Link>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-foreground-800">{anime.title}</span>
            </nav>

            <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr] md:gap-8">
              <div className="w-40 shrink-0 sm:w-48 md:w-[220px]">
                <div className="aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                  <img
                    src={anime.cover}
                    alt={`${anime.title} 封面`}
                    title={`${anime.title} - 动漫详情`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="mt-3 flex items-center justify-between rounded-md bg-background-50 px-3 py-2">
                  <span className="flex items-center gap-1 font-label text-sm text-primary-600">
                    <i className="ri-star-fill text-xs" />
                    {anime.rating.toFixed(1)}
                  </span>
                  <span className="text-[11px] text-foreground-500">{anime.views} 播放</span>
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                    {anime.status}
                  </span>
                  {anime.featured && (
                    <span className="rounded bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-700">
                      本周焦点
                    </span>
                  )}
                  <span className="rounded bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                    {anime.year} 年新番
                  </span>
                </div>

                <h1 className="mt-3 text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
                  {anime.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-600 md:text-base">{anime.subtitle}</p>

                <dl className="mt-5 grid grid-cols-2 gap-y-2.5 text-sm sm:grid-cols-3">
                  <div className="flex items-center gap-1.5">
                    <dt className="text-foreground-500">类型</dt>
                    <dd className="text-foreground-800">{anime.genres.join(" / ")}</dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="text-foreground-500">地区</dt>
                    <dd className="text-foreground-800">{anime.region}</dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="text-foreground-500">集数</dt>
                    <dd className="text-foreground-800">全 {anime.episodes} 集</dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="text-foreground-500">导演</dt>
                    <dd className="truncate text-foreground-800">{anime.director}</dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="text-foreground-500">制作</dt>
                    <dd className="truncate text-foreground-800">{anime.studio}</dd>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <dt className="text-foreground-500">上映</dt>
                    <dd className="truncate text-foreground-800">{anime.releaseDate}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2">
                  {anime.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="whitespace-nowrap rounded-md bg-background-200/70 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      # {tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={scrollToPlayer}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-fill text-base" />
                    开始观看第 {currentEpisode} 集
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleFavorite(anime.id)}
                    className={[
                      "inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-2.5 text-sm transition-colors",
                      favorited
                        ? "border-primary-500 bg-primary-100 text-primary-700"
                        : "border-background-300 bg-background-50 text-foreground-800 hover:border-primary-300 hover:text-primary-700",
                    ].join(" ")}
                  >
                    <i className={favorited ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} />
                    {favorited ? "已收藏" : "加入收藏"}
                  </button>
                  <span className="text-xs text-foreground-500">
                    收藏会保存在本地浏览器，可在分类页「只看我的收藏」中查看。
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
            <div className="space-y-10">
              <section>
                <h2 className="text-lg font-bold text-foreground-950">剧情简介</h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">{anime.intro}</p>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-foreground-500">
                  <span>更新时间：{anime.updateText}</span>
                  <span>制作公司：{anime.studio}</span>
                  <span>导演：{anime.director}</span>
                </div>
              </section>

              <section ref={playerRef}>
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-foreground-950">剧集列表</h2>
                    <p className="mt-1 text-xs text-foreground-500">
                      共 {anime.episodes} 集 · 点击集数切换，当前为第 {currentEpisode} 集
                    </p>
                  </div>
                  <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900">
                    前端演示 · 不含真实播放接口
                  </span>
                </div>

                <div className="relative mt-4 aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-foreground-950">
                  <img
                    src={anime.banner ? anime.banner : anime.cover}
                    alt={`${anime.title} 第 ${currentEpisode} 集演示画面`}
                    className="h-full w-full object-cover opacity-40"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/70 to-foreground-950/40" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-50/40 text-background-50">
                      <i className="ri-play-fill text-2xl" />
                    </span>
                    <p className="mt-4 text-base font-medium text-background-50">
                      {anime.title} · 第 {currentEpisode} 集
                    </p>
                    <p className="mt-1.5 max-w-md text-xs leading-relaxed text-background-300">
                      这是演示画面，用于展示剧集切换效果，本站不提供真实视频播放与播放成功状态。
                    </p>
                    <div className="mt-5 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentEpisode((prev) => Math.max(1, prev - 1))}
                        disabled={currentEpisode <= 1}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-50/30 px-3 py-1.5 text-xs text-background-100 transition-colors hover:bg-background-50/10 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <i className="ri-skip-left-line" />
                        上一集
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentEpisode((prev) => Math.min(anime.episodes, prev + 1))}
                        disabled={currentEpisode >= anime.episodes}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-1.5 text-xs text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        下一集
                        <i className="ri-skip-right-line" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <EpisodeList
                    episodes={anime.episodes}
                    current={currentEpisode}
                    onChange={setCurrentEpisode}
                    title={anime.title}
                  />
                </div>
              </section>

              <CommentSection />
            </div>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="text-base font-bold text-foreground-950">详细信息</h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">原名</dt>
                    <dd className="text-right text-foreground-800">{anime.title}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">地区</dt>
                    <dd className="text-right text-foreground-800">{anime.region}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">年份</dt>
                    <dd className="text-right text-foreground-800">{anime.year}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">集数</dt>
                    <dd className="text-right text-foreground-800">{anime.episodes} 集</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">状态</dt>
                    <dd className="text-right text-foreground-800">{anime.status}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">制作公司</dt>
                    <dd className="text-right text-foreground-800">{anime.studio}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">导演</dt>
                    <dd className="text-right text-foreground-800">{anime.director}</dd>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <dt className="shrink-0 text-foreground-500">上映时间</dt>
                    <dd className="text-right text-foreground-800">{anime.releaseDate}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-bold text-foreground-950">相关推荐</h2>
                  <Link
                    to={`/category?genre=${encodeURIComponent(anime.genres[0])}`}
                    className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
                  >
                    同类作品
                  </Link>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-4">
                  {recommendations.map((item) => (
                    <AnimeCard key={item.id} anime={item} showFavorite={false} />
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}